import { useMemo, useEffect, useState } from "react";
import axios from "axios";
import { EDUTRI_URL } from "../../utils/constants";
import "./EdutriBlurredResult.css";

const API = "https://api.testknock.com/api";

const EdutriBlurredResult = ({ subject, testId, calculateScore }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const localScore = useMemo(() => {
    if (typeof calculateScore === "function") return calculateScore();
    return { score: 0, correctQuestions: 0, incorrectQuestions: 0, unattemptedQuestions: 0 };
  }, [calculateScore]);

  const score = localScore.score || 0;
  const correct = localScore.correctQuestions || 0;
  const incorrect = localScore.incorrectQuestions || 0;
  const unattempted = localScore.unattemptedQuestions || 0;
  const total = 250;

  // Read edutri token passed via URL param (cross-origin: edutri is on different port)
  const edutriToken = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edutri_token") || null;
  }, []);

  const isEdutriLoggedIn = !!edutriToken;

  const edutriResultsUrl = `${EDUTRI_URL}/results?score=${score}&total=${total}&correct=${correct}&incorrect=${incorrect}&unattempted=${unattempted}&subject=${subject || "general_test"}&testId=${testId || "1"}`;

  // If logged in — auto-save score then redirect to results
  useEffect(() => {
    if (!isEdutriLoggedIn || saved || saving) return;

    setSaving(true);
    axios
      .post(
        `${API}/users/edutri-save-score`,
        { subject, testId: String(testId), score, total, correct, incorrect, unattempted },
        { headers: { Authorization: edutriToken } }
      )
      .catch(() => { /* will retry save on results page */ })
      .finally(() => {
        setSaved(true);
        setSaving(false);
        window.location.href = edutriResultsUrl;
      });
  }, [isEdutriLoggedIn]); // eslint-disable-line

  // Encode result in URL so it survives cross-origin redirect (localStorage is per-origin)
  const encodedResult = btoa(JSON.stringify({
    score, total, correct, incorrect, unattempted,
    subject: subject || "general_test",
    testId: testId || "1",
  }));

  const handleSignupClick = () => {
    window.location.href = `${EDUTRI_URL}/signup?result=${encodeURIComponent(encodedResult)}`;
  };

  const handleLoginClick = (e) => {
    e.preventDefault();
    window.location.href = `${EDUTRI_URL}/login?result=${encodeURIComponent(encodedResult)}`;
  };

  const handleViewResults = () => {
    window.location.href = edutriResultsUrl;
  };

  // If logged in, show saving state while we redirect
  if (isEdutriLoggedIn) {
    return (
      <div className="edutri-blurred-container">
        <div className="edutri-score-preview">
          <div className="esp-header">
            <div className="esp-logo">
              <span className="esp-logo-icon">E</span>
              <span className="esp-logo-text">Edu<span>Tri</span> CUET</span>
            </div>
            <h2 className="esp-title">Test Completed! 🎉</h2>
            <p className="esp-subtitle">{saving ? "Saving your score..." : "Redirecting to your results..."}</p>
          </div>
          <div className="esp-score-card">
            <div className="esp-score-circle">
              <svg viewBox="0 0 120 120" width="140" height="140">
                <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" strokeWidth="12" />
                <circle
                  cx="60" cy="60" r="50"
                  fill="none" stroke="url(#grad1)" strokeWidth="12"
                  strokeDasharray={`${Math.max(0, Math.min(314, (score / total) * 314))} 314`}
                  strokeLinecap="round"
                  transform="rotate(-90 60 60)"
                />
                <defs>
                  <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#4F46E5" />
                    <stop offset="100%" stopColor="#7C3AED" />
                  </linearGradient>
                </defs>
                <text x="60" y="55" textAnchor="middle" fontSize="20" fontWeight="800" fill="#111827">{score}</text>
                <text x="60" y="71" textAnchor="middle" fontSize="12" fill="#9CA3AF">/{total}</text>
              </svg>
            </div>
            <div className="esp-stats-row">
              <div className="esp-stat correct"><span className="esp-stat-num">{correct}</span><span className="esp-stat-label">Correct</span></div>
              <div className="esp-stat incorrect"><span className="esp-stat-num">{incorrect}</span><span className="esp-stat-label">Wrong</span></div>
              <div className="esp-stat skipped"><span className="esp-stat-num">{unattempted}</span><span className="esp-stat-label">Skipped</span></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in — show blurred result with signup CTA
  return (
    <div className="edutri-blurred-container">
      <div className="edutri-score-preview">
        <div className="esp-header">
          <div className="esp-logo">
            <span className="esp-logo-icon">E</span>
            <span className="esp-logo-text">Edu<span>Tri</span> CUET</span>
          </div>
          <h2 className="esp-title">You've Completed the Mock Test! 🎉</h2>
          <p className="esp-subtitle">Here's a preview of your score.</p>
        </div>

        <div className="esp-score-card">
          <div className="esp-score-circle">
            <svg viewBox="0 0 120 120" width="140" height="140">
              <circle cx="60" cy="60" r="50" fill="none" stroke="#E5E7EB" strokeWidth="12" />
              <circle
                cx="60" cy="60" r="50"
                fill="none" stroke="url(#grad1)" strokeWidth="12"
                strokeDasharray={`${Math.max(0, Math.min(314, (score / total) * 314))} 314`}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
              <defs>
                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#4F46E5" />
                  <stop offset="100%" stopColor="#7C3AED" />
                </linearGradient>
              </defs>
              <text x="60" y="55" textAnchor="middle" fontSize="20" fontWeight="800" fill="#111827">{score}</text>
              <text x="60" y="71" textAnchor="middle" fontSize="12" fill="#9CA3AF">/{total}</text>
            </svg>
          </div>
          <div className="esp-stats-row">
            <div className="esp-stat correct"><span className="esp-stat-num">{correct}</span><span className="esp-stat-label">Correct</span></div>
            <div className="esp-stat incorrect"><span className="esp-stat-num">{incorrect}</span><span className="esp-stat-label">Wrong</span></div>
            <div className="esp-stat skipped"><span className="esp-stat-num">{unattempted}</span><span className="esp-stat-label">Skipped</span></div>
          </div>
        </div>
      </div>

      <div className="edutri-blurred-section">
        <div className="edutri-blur-overlay">
          <div className="ebo-card">
            <div className="ebo-lock">🔒</div>
            <h3>Unlock Your Full Results</h3>
            <p>See your <strong>rank</strong>, <strong>percentile</strong>, <strong>topper comparison</strong>, sectional analysis, and answer explanations.</p>
            <div className="ebo-perks">
              <span>✓ Your rank among 50,000+ students</span>
              <span>✓ Detailed section-wise breakdown</span>
              <span>✓ Compare with toppers</span>
              <span>✓ 4 more free mock tests</span>
            </div>
            <div className="ebo-buttons">
              <button className="ebo-btn-primary" onClick={handleSignupClick}>
                Sign Up Free — See Full Results
              </button>
              <button className="ebo-btn-secondary" onClick={handleViewResults}>
                View on EduTri →
              </button>
            </div>
            <p className="ebo-note">
              Already have an account?{" "}
              <a href={`${EDUTRI_URL}/login`} onClick={handleLoginClick}>Login to EduTri</a>
            </p>
          </div>
        </div>

        <div className="blurred-analytics-preview">
          <div className="bap-card"><div className="bap-icon">🏆</div><div className="bap-value">#1,248</div><div className="bap-label">Your Rank</div></div>
          <div className="bap-card"><div className="bap-icon">📊</div><div className="bap-value">87.4%ile</div><div className="bap-label">Percentile</div></div>
          <div className="bap-card"><div className="bap-icon">⭐</div><div className="bap-value">238/250</div><div className="bap-label">Topper Score</div></div>
        </div>
      </div>

      <div className="edutri-promo">
        <div className="promo-content">
          <div className="promo-text">
            <h3>Want 15 Mock Tests per Subject?</h3>
            <p>Get the <strong>CUET Complete Pack</strong> — 5 subjects × 15 mock tests + prep modules for just <strong>₹750</strong> (₹10/test)</p>
          </div>
          <a href={`${EDUTRI_URL}/pricing`} className="promo-btn">View Plans — ₹750 →</a>
        </div>
      </div>
    </div>
  );
};

export default EdutriBlurredResult;
